import React, { useState } from 'react';
import { WalletConnection } from './WalletConnection';
import { EvmAddressLinker } from './EvmAddressLinker';
import { useArConnect } from '../hooks/useArConnect';

function HomePage() {
  const [currentStep, setCurrentStep] = useState<'wallet' | 'evm'>('wallet');
  const arConnectState = useArConnect();

  const handleContinue = () => {
    setCurrentStep('evm');
  };

  const handleBack = () => {
    setCurrentStep('wallet');
  };

  return (
    <div className="min-h-screen relative">
      {/* Grid overlay */}
      <div className="fixed inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 212, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 212, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">
            Arweave-EVM Address Linker
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Connect your <span className="font-semibold text-white">Arweave</span> wallet and link your 
            <span className="font-semibold text-white"> EVM address</span>
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${arConnectState.isConnected ? 'text-green-400' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                arConnectState.isConnected 
                  ? 'border-green-400 bg-green-400/20 text-green-400' 
                  : 'border-gray-400 bg-gray-400/20'
              }`}>
                {arConnectState.isConnected ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="text-sm font-medium">1</span>
                )}
              </div>
              <span className="text-sm font-medium">Arweave Wallet</span>
            </div>
            
            <div className={`w-8 h-0.5 ${arConnectState.isConnected ? 'bg-green-400' : 'bg-gray-600'}`}></div>
            
            <div className={`flex items-center space-x-2 ${arConnectState.evmAddress ? 'text-green-400' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                arConnectState.evmAddress 
                  ? 'border-green-400 bg-green-400/20 text-green-400' 
                  : 'border-gray-400 bg-gray-400/20'
              }`}>
                {arConnectState.evmAddress ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="text-sm font-medium">2</span>
                )}
              </div>
              <span className="text-sm font-medium">EVM Address</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-2xl mx-auto">
          {currentStep === 'wallet' ? (
            <WalletConnection 
              arConnectState={arConnectState} 
              onContinue={handleContinue}
              showContinueButton={true}
            />
          ) : (
            <EvmAddressLinker 
              arConnectState={arConnectState} 
              onBack={handleBack}
              showBackButton={true}
            />
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-2 text-sm text-gray-400">
            <span>Powered by</span>
            <span className="font-semibold underline"><a href="https://decent.land">Decent Land Labs</a></span>
            <div className="w-1 h-1 bg-cyan-400 rounded-full"></div>
            <span className="font-semibold underline"><a href="https://ao.arweave.dev">AO</a></span>
          </div>
        </div>
      </div>

      {/* Floating orbs */}
      <div className="fixed top-20 left-10 w-32 h-32 bg-cyan-400 rounded-full opacity-5 blur-xl"></div>
      <div className="fixed bottom-20 right-10 w-40 h-40 bg-purple-500 rounded-full opacity-5 blur-xl"></div>
      <div className="fixed top-1/2 left-1/4 w-24 h-24 bg-pink-500 rounded-full opacity-5 blur-xl"></div>
    </div>
  );
}

export default HomePage;
